import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, AuthTokens } from '@/types'
import { authApi } from './api'
import { identifyUser, resetUser } from '@/services/tracking'

interface SignupData {
  username: string
  password: string
  full_name: string
  dob: string
  gender: string
  phone?: string
  address?: string
  referral_code: string
}

interface AuthState {
  user: User | null
  tokens: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean

  login: (username: string, password: string) => Promise<void>
  signup: (data: SignupData) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  setTokens: (tokens: AuthTokens) => void
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: true,

      login: async (username: string, password: string) => {
        try {
          const tokens = await authApi.login(username, password)
          localStorage.setItem('access_token', tokens.access_token)
          localStorage.setItem('refresh_token', tokens.refresh_token)

          const user = await authApi.getMe()

          // Identify user for analytics
          if (user?.id) {
            identifyUser(user.id, {
              username: user.username,
              role: user.role,
            })
          }

          set({
            user,
            tokens,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      signup: async (data) => {
        try {
          await authApi.signup(data)
          // Auto-login after signup
          await get().login(data.username, data.password)
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      logout: async () => {
        try {
          await authApi.logout()
        } catch (_error) {
          // Continue with local logout even if API fails
        }

        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')

        // Reset user analytics
        resetUser()

        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
        })
      },

      checkAuth: async () => {
        const token = localStorage.getItem('access_token')
        if (!token) {
          set({ isLoading: false, isAuthenticated: false })
          return
        }

        try {
          const user = await authApi.getMe()

          // Identify returning user for analytics
          if (user?.id) {
            identifyUser(user.id, {
              username: user.username,
              role: user.role,
            })
          }

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch (_error) {
          // Token is invalid
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },

      setTokens: (tokens: AuthTokens) => {
        localStorage.setItem('access_token', tokens.access_token)
        localStorage.setItem('refresh_token', tokens.refresh_token)
        set({ tokens })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
)
